import LocationChoicesList from "./LocationChoicesList";
import { SearchOutlined } from "@ant-design/icons";
import { GOOGLE_MAPS_CONFIG } from "@app/configs/map";
import { APIProvider, Map, Marker, useMap } from "@vis.gl/react-google-maps";
import { AutoComplete, Button } from "antd";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    
  @media screen and (max-width: 767px){
    border-radius: 0;
  }
`;

const MapWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const MapTypeButton = styled(Button)`
  position: absolute;
  bottom: 20px;
  right: 60px;
  z-index: 1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  height: auto;
`;

const MapTypeThumbnail = styled.div<{ imageUrl: string }>`
  width: 60px;
  height: 60px;
  border-radius: 4px;
  background-image: url(${(props) => props.imageUrl});
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
`;

const MapTypeLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  position: absolute;
  top: 5px;
  left: 50%;
  transform: translateX(-50%);
  color: #fff;
`;

const SearchBoxContainer = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  right: 50px;
  z-index: 99;
  max-width: 360px;

  @media (max-width: 524px) {
    max-width: 100%;
    right: 10px;
  }
`;

const StyledAutoComplete = styled(AutoComplete)`
  width: 100%;
  height: 40px !important;

  .ant-select-selector {
    border-radius: 20px !important;
    height: 40px !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

    .ant-select-selection-search-input {
      height: 38px !important;
      overflow: hidden !important;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: calc(100% - 20px) !important;
    }

    .ant-select-selection-placeholder {
      line-height: 38px !important;
    }
  }

  .ant-select-selection-item {
    line-height: 38px !important;
  }
`;

interface LocationChoice {
  ref_id: string;
  display: string;
}

interface GoogleMapProps {
  onMapLoad?: (map: google.maps.Map) => void;
  searchQuery?: string;
  onLocationSelect?: (location: { lat: number; lng: number }) => void;
  onCurrentLocationFound?: (location: { lat: number; lng: number }) => void;
  className?: string;
  // Directions props
  origin?: { lat: number; lng: number } | string;
  destination?: { lat: number; lng: number } | string;
  autocompleteSearch?: string;
  travelMode?:
    | google.maps.TravelMode
    | "DRIVING"
    | "WALKING"
    | "BICYCLING"
    | "TRANSIT";
  onRouteCalculated?: (route: google.maps.DirectionsResult) => void;
  // Search props
  onDestinationChange?: (
    destination: string | { lat: number; lng: number } | null
  ) => void;
  // Location choices props
  locationChoices?: LocationChoice[];
  onLocationChoiceSelect?: (choice: LocationChoice) => void;
}

// Autocomplete Search Box Component with Ant Design UI
const AutocompleteSearchBox: React.FC<{
  onPlaceSelected: (place: google.maps.places.PlaceResult | null) => void;
  placeholder?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  currentLocation?: { lat: number; lng: number } | null;
}> = ({
  onPlaceSelected,
  placeholder = "Tìm kiếm địa điểm...",
  value,
  onValueChange,
  currentLocation,
}) => {
  const [options, setOptions] = useState<
    Array<{ value: string; label: React.ReactNode }>
  >([]);
  const [isApiReady, setIsApiReady] = useState(false);
  const autocompleteServiceRef =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(
    null
  );
  const requestTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Wait for Google Maps API to be ready
  useEffect(() => {
    const checkApiReady = () => {
      if (window.google?.maps?.places) {
        setIsApiReady(true);
        return true;
      }
      return false;
    };

    if (checkApiReady()) {
      return;
    }

    const interval = setInterval(() => {
      if (checkApiReady()) {
        clearInterval(interval);
      }
    }, 100);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!isApiReady) {
        console.warn("Google Maps Places API not loaded after 10 seconds");
      }
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  // Initialize services when API is ready
  useEffect(() => {
    if (!isApiReady) return;

    if (!autocompleteServiceRef.current) {
      autocompleteServiceRef.current =
        new google.maps.places.AutocompleteService();
    }

    // Create a dummy div for PlacesService (required by API)
    const dummyDiv = document.createElement("div");
    if (!placesServiceRef.current) {
      placesServiceRef.current = new google.maps.places.PlacesService(dummyDiv);
    }

    return () => {
      // Cleanup if needed
    };
  }, [isApiReady]);

  // Keep callback ref updated
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  useEffect(() => {
    onPlaceSelectedRef.current = onPlaceSelected;
  }, [onPlaceSelected]);

  // Track previous value to detect external changes
  const prevValueRef = useRef<string | undefined>(value);
  const isInternalChangeRef = useRef<boolean>(false);
  const isInitialMountRef = useRef<boolean>(true);
  const prevApiReadyRef = useRef<boolean>(isApiReady);

  // Core search function that performs the API call
  const performSearch = useCallback(
    (searchText: string, shouldNotifyParent: boolean = true) => {
      if (!searchText) {
        setOptions([]);
        if (shouldNotifyParent) {
          onPlaceSelectedRef.current(null);
        }
        return;
      }

      if (!autocompleteServiceRef.current) {
        setOptions([]);
        return;
      }

      // Clear previous timeout
      if (requestTimeoutRef.current) {
        clearTimeout(requestTimeoutRef.current);
      }

      // Debounce API calls
      requestTimeoutRef.current = setTimeout(() => {
        if (!autocompleteServiceRef.current) return;

        const request: google.maps.places.AutocompletionRequest = {
          input: searchText,
          componentRestrictions: { country: "vn" },
          types: ["establishment", "geocode"],
        };

        // Add location bias if currentLocation is available
        if (currentLocation) {
          request.location = new google.maps.LatLng(
            currentLocation.lat,
            currentLocation.lng
          );
          request.radius = 50000; // 50km radius - prioritize results within this radius
        }

        autocompleteServiceRef.current.getPlacePredictions(
          request,
          (
            predictions: google.maps.places.AutocompletePrediction[] | null,
            status: google.maps.places.PlacesServiceStatus
          ) => {
            if (
              status === google.maps.places.PlacesServiceStatus.OK &&
              predictions
            ) {
              const newOptions = predictions.map((prediction) => ({
                value: prediction.place_id,
                label: (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontWeight: 500 }}>
                      {prediction.structured_formatting.main_text}
                    </span>
                    {prediction.structured_formatting.secondary_text && (
                      <span style={{ fontSize: "12px", color: "#999" }}>
                        {prediction.structured_formatting.secondary_text}
                      </span>
                    )}
                  </div>
                ),
                prediction,
              }));
              setOptions(newOptions);
            } else {
              setOptions([]);
            }
          }
        );
      }, 300);
    },
    [currentLocation]
  );

  // Trigger search when value changes externally (not from user typing) or when API becomes ready
  useEffect(() => {
    // Don't trigger if API is not ready
    if (!isApiReady || !autocompleteServiceRef.current) {
      prevApiReadyRef.current = isApiReady;
      return;
    }

    // Check if API just became ready
    const apiJustBecameReady = !prevApiReadyRef.current && isApiReady;
    prevApiReadyRef.current = isApiReady;

    // On initial mount, trigger search if value exists
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      prevValueRef.current = value;
      // Trigger search on initial mount if value exists
      if (value && value.trim()) {
        performSearch(value, false);
      }
      return;
    }

    // If API just became ready and value exists, trigger search even if value hasn't changed
    if (
      apiJustBecameReady &&
      value &&
      value.trim() &&
      !isInternalChangeRef.current
    ) {
      performSearch(value, false);
      return;
    }

    // Skip if value hasn't changed
    if (prevValueRef.current === value) {
      return;
    }

    // Skip if this change was triggered internally (from handleSearch or handleSelect)
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      prevValueRef.current = value;
      return;
    }

    // This is an external change, trigger search without notifying parent
    if (value && value.trim()) {
      performSearch(value, false);
    } else {
      setOptions([]);
    }

    prevValueRef.current = value;
  }, [value, performSearch, isApiReady]);

  // Handle search input change
  const handleSearch = useCallback(
    (searchText: string) => {
      // Mark as internal change
      isInternalChangeRef.current = true;
      if (onValueChange) {
        onValueChange(searchText);
      }

      // Perform search
      performSearch(searchText, true);
    },
    [onValueChange, performSearch]
  );

  // Handle option selection
  const handleSelect = useCallback(
    (value: unknown, option: any) => {
      const placeId = value as string;
      if (!placesServiceRef.current || !option?.prediction) return;

      // Mark as internal change
      isInternalChangeRef.current = true;

      const request: google.maps.places.PlaceDetailsRequest = {
        placeId: placeId,
        fields: ["name", "geometry", "formatted_address", "place_id"],
      };

      placesServiceRef.current.getDetails(
        request,
        (
          place: google.maps.places.PlaceResult | null,
          status: google.maps.places.PlacesServiceStatus
        ) => {
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            place &&
            place.geometry
          ) {
            onPlaceSelectedRef.current(place);
            if (onValueChange) {
              onValueChange(place.formatted_address || place.name || "");
            }
          } else {
            onPlaceSelectedRef.current(null);
          }
          // Reset flag after processing
          setTimeout(() => {
            isInternalChangeRef.current = false;
          }, 100);
        }
      );
    },
    [onValueChange]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (requestTimeoutRef.current) {
        clearTimeout(requestTimeoutRef.current);
      }
    };
  }, []);

  return (
    <StyledAutoComplete
      value={value}
      options={options}
      onSearch={handleSearch}
      onSelect={handleSelect}
      placeholder={placeholder}
      allowClear
      suffixIcon={<SearchOutlined />}
      notFoundContent={value ? "Không tìm thấy kết quả" : null}
      filterOption={false} // Disable default filtering, use Google Places API instead
    />
  );
};

// Map Type Control Component
const MapTypeControl: React.FC = () => {
  const map = useMap();
  const [mapType, setMapType] = useState<google.maps.MapTypeId>(
    google.maps.MapTypeId.ROADMAP
  );
  const [mapCenter, setMapCenter] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [mapZoom, setMapZoom] = useState<number>(10);

  useEffect(() => {
    if (!map) return;

    // Set initial map type
    map.setMapTypeId(mapType);

    // Listen to map center and zoom changes
    const updateMapInfo = () => {
      const center = map.getCenter();
      if (center) {
        setMapCenter({ lat: center.lat(), lng: center.lng() });
      }
      setMapZoom(map.getZoom() || 10);
    };

    // Initial update
    updateMapInfo();

    // Listen to center and zoom changes
    const centerListener = map.addListener("center_changed", updateMapInfo);
    const zoomListener = map.addListener("zoom_changed", updateMapInfo);

    return () => {
      google.maps.event.removeListener(centerListener);
      google.maps.event.removeListener(zoomListener);
    };
  }, [map, mapType]);

  const toggleMapType = useCallback(() => {
    if (!map) return;

    const newMapType =
      mapType === google.maps.MapTypeId.ROADMAP
        ? google.maps.MapTypeId.SATELLITE
        : google.maps.MapTypeId.ROADMAP;

    setMapType(newMapType);
    map.setMapTypeId(newMapType);
  }, [map, mapType]);

  // Generate thumbnail URL for the opposite map type
  const getThumbnailUrl = useCallback(() => {
    // Use current center or fallback to default center
    const center = mapCenter || {
      lat: GOOGLE_MAPS_CONFIG.DEFAULT_CENTER.LAT,
      lng: GOOGLE_MAPS_CONFIG.DEFAULT_CENTER.LNG,
    };

    const oppositeMapType =
      mapType === google.maps.MapTypeId.ROADMAP ? "satellite" : "roadmap";

    const baseUrl = "https://maps.googleapis.com/maps/api/staticmap";
    const params = new URLSearchParams({
      center: `${center.lat},${center.lng}`,
      zoom: mapZoom.toString(),
      size: "48x48",
      maptype: oppositeMapType,
      key: GOOGLE_MAPS_CONFIG.API_KEY,
      scale: "2", // For retina displays
    });

    return `${baseUrl}?${params.toString()}`;
  }, [mapCenter, mapZoom, mapType]);

  if (!map) return null;

  const isSatellite = mapType === google.maps.MapTypeId.SATELLITE;
  const thumbnailUrl = getThumbnailUrl();
  const label = isSatellite ? "Bản đồ" : "Vệ tinh";

  return (
    <MapTypeButton onClick={toggleMapType} type="default">
      {thumbnailUrl && <MapTypeThumbnail imageUrl={thumbnailUrl} />}
      <MapTypeLabel>{label}</MapTypeLabel>
    </MapTypeButton>
  );
};

// Internal component that uses the map instance
const MapContent: React.FC<{
  onMapLoad?: (map: google.maps.Map) => void;
  searchQuery?: string;
  onLocationSelect?: (location: { lat: number; lng: number }) => void;
  onCurrentLocationFound?: (location: { lat: number; lng: number }) => void;
  getCurrentLocation: () => void;
  currentLocation: { lat: number; lng: number } | null;
  setSearchResult: (
    result: { lat: number; lng: number; name?: string } | null
  ) => void;
  searchResult: { lat: number; lng: number; name?: string } | null;
  origin?: { lat: number; lng: number } | string;
  destination?: { lat: number; lng: number } | string;
  travelMode?:
    | google.maps.TravelMode
    | "DRIVING"
    | "WALKING"
    | "BICYCLING"
    | "TRANSIT";
  onRouteCalculated?: (route: google.maps.DirectionsResult) => void;
  onDestinationChange?: (
    destination: string | { lat: number; lng: number } | null
  ) => void;
  onRouteStatusChange?: (hasRoute: boolean) => void;
}> = ({
  onMapLoad,
  searchQuery,
  onLocationSelect,
  onCurrentLocationFound,
  getCurrentLocation,
  currentLocation,
  setSearchResult,
  searchResult,
  origin,
  destination,
  travelMode,
  onRouteCalculated,
  onRouteStatusChange,
}) => {
  const map = useMap();
  const directionsRendererRef =
    React.useRef<google.maps.DirectionsRenderer | null>(null);
  const lastRequestRef = React.useRef<string | null>(null);
  const onRouteCalculatedRef = React.useRef(onRouteCalculated);

  // Keep callback ref updated
  useEffect(() => {
    onRouteCalculatedRef.current = onRouteCalculated;
  }, [onRouteCalculated]);

  // Configure map controls
  useEffect(() => {
    if (!map) return;

    // Enable default UI controls
    map.setOptions({
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: false, // Use custom map type control instead
      scaleControl: true,
      streetViewControl: false,
      fullscreenControl: false,
      rotateControl: true,
    });
  }, [map]);

  // Notify parent when map is loaded
  useEffect(() => {
    if (map && onMapLoad) {
      onMapLoad(map);
      // Get current location when map loads
      getCurrentLocation();
    }
  }, [map, onMapLoad, getCurrentLocation]);

  // Update map center when current location changes (only if no route is displayed)
  useEffect(() => {
    if (
      map &&
      currentLocation &&
      !directionsRendererRef.current?.getDirections()
    ) {
      map.setCenter(currentLocation);
      map.setZoom(15);
    }
  }, [map, currentLocation]);

  // Zoom to search result when it changes
  useEffect(() => {
    if (map && searchResult) {
      // If there's a route, clear it first
      if (directionsRendererRef.current?.getDirections()) {
        directionsRendererRef.current.setDirections(null);
        if (onRouteStatusChange) {
          onRouteStatusChange(false);
        }
      }
      // Zoom to the new location
      map.setCenter({ lat: searchResult.lat, lng: searchResult.lng });
      map.setZoom(15);
    }
  }, [map, searchResult, onRouteStatusChange]);

  // Initialize DirectionsRenderer
  useEffect(() => {
    if (!map) return;

    if (!directionsRendererRef.current) {
      directionsRendererRef.current = new google.maps.DirectionsRenderer({
        map: map,
        suppressMarkers: false, // Show default markers
        preserveViewport: false, // Auto-fit to route
      });
    }

    return () => {
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(null);
      }
    };
  }, [map]);

  // Calculate and display route
  useEffect(() => {
    if (!map || !destination) {
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setDirections(null);
      }
      lastRequestRef.current = null;
      if (onRouteStatusChange) {
        onRouteStatusChange(false);
      }
      return;
    }

    // Use current location as origin if origin is not provided
    const effectiveOrigin =
      origin || (currentLocation ? currentLocation : null);

    if (!effectiveOrigin) {
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setDirections(null);
      }
      lastRequestRef.current = null;
      return;
    }

    // Helper to normalize address string for comparison
    const normalizeAddress = (
      addr: string | { lat: number; lng: number }
    ): string => {
      if (typeof addr === "string") {
        // Normalize address string: trim, lowercase, remove extra spaces
        return addr.trim().toLowerCase().replace(/\s+/g, " ");
      }
      // For coordinates, round to avoid minor GPS fluctuations
      return `${Math.round(addr.lat * 1000)}_${Math.round(addr.lng * 1000)}`;
    };

    // Create request key for comparison
    const originKey = normalizeAddress(effectiveOrigin);
    const destKey = normalizeAddress(destination);
    const modeKey =
      typeof travelMode === "string" ? travelMode : travelMode || "DRIVING";
    const requestKey = `${originKey}|${destKey}|${modeKey}`;

    // Skip if same request
    if (lastRequestRef.current === requestKey) {
      return;
    }

    // Re-check (in case values changed)
    const effectiveOriginNow =
      origin || (currentLocation ? currentLocation : null);
    if (!effectiveOriginNow) return;

    const originKeyNow = normalizeAddress(effectiveOriginNow);
    const destKeyNow = normalizeAddress(destination);
    const modeKeyNow =
      typeof travelMode === "string" ? travelMode : travelMode || "DRIVING";
    const requestKeyNow = `${originKeyNow}|${destKeyNow}|${modeKeyNow}`;

    // Skip if same as last successful request
    if (lastRequestRef.current === requestKeyNow) {
      return;
    }

    if (!window.google?.maps?.DirectionsService) {
      console.error("Google Maps Directions API not loaded");
      return;
    }

    const directionsService = new google.maps.DirectionsService();
    const mode =
      typeof travelMode === "string"
        ? google.maps.TravelMode[
            travelMode as keyof typeof google.maps.TravelMode
          ] || google.maps.TravelMode.DRIVING
        : travelMode || google.maps.TravelMode.DRIVING;

    const request: google.maps.DirectionsRequest = {
      origin:
        typeof effectiveOriginNow === "string"
          ? effectiveOriginNow
          : effectiveOriginNow,
      destination: typeof destination === "string" ? destination : destination,
      travelMode: mode,
    };

    directionsService.route(request, (result, status) => {
      if (
        status === google.maps.DirectionsStatus.OK &&
        result &&
        directionsRendererRef.current
      ) {
        // Save request key only after successful call
        lastRequestRef.current = requestKeyNow;

        directionsRendererRef.current.setDirections(result);

        const bounds = new google.maps.LatLngBounds();
        result.routes[0].legs.forEach((leg) => {
          bounds.extend(leg.start_location);
          bounds.extend(leg.end_location);
        });
        map.fitBounds(bounds);

        if (onRouteCalculatedRef.current) {
          onRouteCalculatedRef.current(result);
        }

        if (onRouteStatusChange) {
          onRouteStatusChange(true);
        }
      } else {
        console.error("Directions request failed:", status);
        if (directionsRendererRef.current) {
          directionsRendererRef.current.setDirections(null);
        }
        if (onRouteStatusChange) {
          onRouteStatusChange(false);
        }
      }
    });
  }, [map, origin, destination, travelMode, currentLocation]);

  // Handle search query
  useEffect(() => {
    if (!map || !searchQuery) {
      setSearchResult(null);
      return;
    }

    if (!window.google?.maps?.places) {
      console.error("Google Maps Places API not loaded");
      return;
    }

    const service = new google.maps.places.PlacesService(map);
    const request: google.maps.places.FindPlaceFromQueryRequest = {
      query: searchQuery,
      fields: ["name", "geometry", "formatted_address"],
    };

    service.findPlaceFromQuery(
      request,
      (
        results: google.maps.places.PlaceResult[] | null,
        status: google.maps.places.PlacesServiceStatus
      ) => {
        if (
          status === google.maps.places.PlacesServiceStatus.OK &&
          results &&
          results.length > 0
        ) {
          const place = results[0];
          const location = place.geometry?.location;

          if (location) {
            let lat: number;
            let lng: number;

            if (typeof location.lat === "function") {
              lat = location.lat();
              lng = location.lng();
            } else {
              lat = (location as unknown as google.maps.LatLngLiteral).lat;
              lng = (location as unknown as google.maps.LatLngLiteral).lng;
            }

            setSearchResult({
              lat,
              lng,
              name: place.name || searchQuery,
            });

            if (map) {
              map.setCenter({ lat, lng });
              map.setZoom(15);
            }
          }
        } else {
          setSearchResult(null);
        }
      }
    );
  }, [map, searchQuery, setSearchResult]);

  return null;
};

const GoogleMap: React.FC<GoogleMapProps> = ({
  onMapLoad,
  searchQuery,
  onLocationSelect,
  onCurrentLocationFound,
  className,
  origin,
  destination,
  autocompleteSearch,
  travelMode,
  onRouteCalculated,
  onDestinationChange,
  locationChoices,
  onLocationChoiceSelect,
}) => {
  const [currentLocation, setCurrentLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [searchResult, setSearchResult] = useState<{
    lat: number;
    lng: number;
    name?: string;
  } | null>(null);

  // Map default center and zoom
  const defaultCenter = useMemo(
    () => ({
      lat: GOOGLE_MAPS_CONFIG.DEFAULT_CENTER.LAT,
      lng: GOOGLE_MAPS_CONFIG.DEFAULT_CENTER.LNG,
    }),
    []
  );

  const defaultZoom = GOOGLE_MAPS_CONFIG.DEFAULT_ZOOM;
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [selectedLocationChoice, setSelectedLocationChoice] =
    useState<LocationChoice | null>(null);
  const [autocompleteSelectedLocation, setAutocompleteSelectedLocation] =
    useState<LocationChoice | null>(null);
  const [hasRoute, setHasRoute] = useState<boolean>(false);
  const isUserTypingRef = useRef<boolean>(false);

  // Handle route status change
  const handleRouteStatusChange = useCallback((hasRouteValue: boolean) => {
    setHasRoute(hasRouteValue);
    // Clear autocomplete selected location only when route is successfully calculated and displayed
    // Don't clear it when route is cleared (hasRouteValue = false) to allow LocationChoicesList to show
    if (hasRouteValue) {
      setAutocompleteSelectedLocation(null);
    }
  }, []);

  // Handle location choice selection
  const handleLocationChoiceSelect = useCallback(
    (choice: LocationChoice) => {
      setSelectedLocationChoice(choice);

      // Use the display text to search for the location
      if (choice.display) {
        // Parse display to get the searchable address
        // Format: "Name,Address,City,Province" - we'll use the full display text
        const searchText = choice.display;

        // Update search input first
        setSearchInputValue(searchText);

        // Update destination to trigger route calculation
        if (onDestinationChange) {
          onDestinationChange(searchText);
        }

        // Notify parent if callback provided
        if (onLocationChoiceSelect) {
          onLocationChoiceSelect(choice);
        }
      }
    },
    [onDestinationChange, onLocationChoiceSelect]
  );

  // Handle place selection from Autocomplete
  const handlePlaceSelected = useCallback(
    (place: google.maps.places.PlaceResult | null) => {
      // Mark that user is interacting with autocomplete
      isUserTypingRef.current = true;

      if (!place || !place.geometry) {
        // Clear destination if place is null
        if (onDestinationChange) {
          onDestinationChange(null);
        }
        setSearchResult(null);
        setAutocompleteSelectedLocation(null);
        // Reset flag after a short delay
        setTimeout(() => {
          isUserTypingRef.current = false;
        }, 100);
        return;
      }

      const location = place.geometry.location;
      if (!location) {
        setTimeout(() => {
          isUserTypingRef.current = false;
        }, 100);
        return;
      }

      let lat: number;
      let lng: number;

      if (typeof location.lat === "function") {
        lat = location.lat();
        lng = location.lng();
      } else {
        lat = (location as unknown as google.maps.LatLngLiteral).lat;
        lng = (location as unknown as google.maps.LatLngLiteral).lng;
      }

      // Update search result
      setSearchResult({
        lat,
        lng,
        name: place.name || place.formatted_address,
      });

      // Create LocationChoice from selected place (will be cleared when route is displayed)
      const displayText = place.formatted_address || place.name || "";
      if (displayText) {
        setAutocompleteSelectedLocation({
          ref_id: place.place_id || `autocomplete_${Date.now()}`,
          display: displayText,
        });
      }

      // Update search input value first
      setSearchInputValue(place.formatted_address || place.name || "");

      // If there's an active route, clear it first by setting destination to null
      // Don't calculate new route immediately - let user click "Đường đi" from LocationChoicesList
      if (hasRoute && onDestinationChange) {
        // Clear destination to clear the route
        onDestinationChange(null);
      } else if (onDestinationChange) {
        // If no route, update destination immediately to calculate route
        onDestinationChange(place.formatted_address || { lat, lng });
      }

      // Reset flag after a short delay to allow destination update to complete
      setTimeout(() => {
        isUserTypingRef.current = false;
      }, 300);
    },
    [onDestinationChange, hasRoute]
  );

  // Update search input when destination changes externally (only if user is not typing)
  useEffect(() => {
    // Don't update if user is currently interacting with autocomplete
    if (isUserTypingRef.current) {
      return;
    }

    setSearchInputValue(autocompleteSearch || "");
  }, [autocompleteSearch]);

  // Merge locationChoices with autocomplete selected location
  const mergedLocationChoices = useMemo(() => {
    const choices = locationChoices || [];
    // Add autocomplete selected location if it's not already in the list
    // Show it even when route is displayed (route will be cleared when user selects new location)
    if (autocompleteSelectedLocation) {
      // Check if autocomplete location is already in the list
      const isAlreadyInList = choices.some(
        (choice) =>
          choice.ref_id === autocompleteSelectedLocation.ref_id ||
          choice.display === autocompleteSelectedLocation.display
      );
      if (!isAlreadyInList) {
        return [autocompleteSelectedLocation, ...choices];
      }
    }
    return choices;
  }, [locationChoices, autocompleteSelectedLocation]);

  // Handle search input value change from user typing
  const handleSearchInputChange = useCallback((value: string) => {
    // Mark that user is typing
    isUserTypingRef.current = true;
    setSearchInputValue(value);

    // Reset flag after user stops typing (debounce)
    setTimeout(() => {
      isUserTypingRef.current = false;
    }, 500);
  }, []);

  // Function to get current location
  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      console.log("Geolocation is not supported by this browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const location = { lat: latitude, lng: longitude };
        setCurrentLocation(location);

        // Notify parent component about current location
        if (onCurrentLocationFound) {
          onCurrentLocationFound(location);
        }
      },
      (error) => {
        console.log("Error getting location:", error);
        // Fallback to default center if geolocation fails
        const fallbackLocation = {
          lat: GOOGLE_MAPS_CONFIG.DEFAULT_COORDINATES.LAT,
          lng: GOOGLE_MAPS_CONFIG.DEFAULT_COORDINATES.LNG,
        };
        setCurrentLocation(fallbackLocation);

        // Notify parent component about current location
        if (onCurrentLocationFound) {
          onCurrentLocationFound(fallbackLocation);
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes
      }
    );
  }, [onCurrentLocationFound]);

  return (
    <MapContainer className={className}>
      <APIProvider
        apiKey={GOOGLE_MAPS_CONFIG.API_KEY}
        libraries={["places", "geometry"]}
      >
        <MapWrapper>
          <SearchBoxContainer>
            <AutocompleteSearchBox
              onPlaceSelected={handlePlaceSelected}
              placeholder="Tìm kiếm địa điểm hoặc chỉ đường..."
              value={searchInputValue}
              onValueChange={handleSearchInputChange}
              currentLocation={currentLocation}
            />
          </SearchBoxContainer>
          <Map
            defaultCenter={defaultCenter}
            defaultZoom={defaultZoom}
            onClick={(e) => {
              if (e.detail.latLng && onLocationSelect) {
                const lat = e.detail.latLng.lat;
                const lng = e.detail.latLng.lng;
                onLocationSelect({ lat, lng });
              }
            }}
          >
            <MapContent
              onMapLoad={onMapLoad}
              searchQuery={searchQuery}
              onLocationSelect={onLocationSelect}
              onCurrentLocationFound={onCurrentLocationFound}
              getCurrentLocation={getCurrentLocation}
              currentLocation={currentLocation}
              setSearchResult={setSearchResult}
              searchResult={searchResult}
              origin={origin}
              destination={destination}
              travelMode={travelMode}
              onRouteCalculated={onRouteCalculated}
              onDestinationChange={onDestinationChange}
              onRouteStatusChange={handleRouteStatusChange}
            />

            {/* Map Type Control */}
            {/*<MapTypeControl />*/}

            {/* Current location marker */}
            {currentLocation && (
              <Marker
                position={currentLocation}
                title="Vị trí hiện tại"
                icon={{
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 10,
                  fillColor: "#007bff",
                  fillOpacity: 1,
                  strokeColor: "#ffffff",
                  strokeWeight: 3,
                }}
              />
            )}

            {/* Search result marker */}
            {searchResult && (
              <Marker
                position={{ lat: searchResult.lat, lng: searchResult.lng }}
                title={searchResult.name}
                icon={{
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 8,
                  fillColor: "#ff6b6b",
                  fillOpacity: 1,
                  strokeColor: "#ffffff",
                  strokeWeight: 2,
                }}
              />
            )}
          </Map>
          {/* <LocationButton
            type="primary"
            icon={<AimOutlined />}
            onClick={getCurrentLocation}
            title="Tìm vị trí hiện tại"
          /> */}

          {/* Location Choices List */}
          {mergedLocationChoices && mergedLocationChoices.length > 0 && (
            <LocationChoicesList
              locationChoices={mergedLocationChoices}
              onLocationSelect={handleLocationChoiceSelect}
            />
          )}
        </MapWrapper>
      </APIProvider>
    </MapContainer>
  );
};

export default GoogleMap;
